// Recording narration in pieces (and voice memos, as a single piece), for the
// composer's docked recorder. Framework-free: the composer binds it to React
// with useRecordingSession.
//
// A session works on a list of pieces (clips), each starting at a narrated
// block, in reading order. It starts from the saved narration's pieces, and
// recording covers a stretch [from, until) of the blocks (until null: to the
// end): the pieces that started there are set aside and new ones take their
// place. One microphone stays open for the whole run, so "next paragraph"
// closes one take and opens the next in the same instant, with no new
// permission prompt. Each piece is analysed and uploaded in the background as
// soon as it closes, so saving only waits for what is still in flight.

import type { NarrationClip } from '../../lib/types'
import { assertUploadableAudio, formatLimit } from '../../lib/audio'
import { analyseAudio } from '../../lib/peaks'
import { nextInOrder, spliceClips } from '../../lib/narration'
import { newBlockId } from '../../lib/blocks'
import { errorText } from '../../components/ui'
import { openMicrophone, toRecorderError, type Microphone, type Recorder, type RecorderErrorCode } from '../../lib/recorder'

export type RecordPhase = 'idle' | 'starting' | 'recording' | 'paused' | 'finishing' | 'review' | 'saving'

export interface DraftClip extends NarrationClip {
  /** recording → processing (finishing and measuring) → ready. */
  status: 'recording' | 'processing' | 'ready'
  upload: 'waiting' | 'uploading' | 'done' | 'failed'
  /** For playing it back before (and while) it uploads. */
  localUrl: string | null
  blob: Blob | null
  /** Recorded in this session (not saved yet). */
  fresh: boolean
}

export interface SessionError {
  message: string
  /** Set for microphone problems (e.g. 'insecure' offers a file instead). */
  code?: RecorderErrorCode
}

export interface SessionConfig {
  /** The narrated blocks' ids in reading order, now (blocks can change while the session is open). */
  order(): string[]
  /** Upload one piece; resolves to its public URL. */
  upload(blob: Blob): Promise<string>
  /** Longest single piece, in seconds; recording stops there. */
  maxClipSeconds: number
}

const saved = (c: NarrationClip): DraftClip => ({ ...c, status: 'ready', upload: 'done', localUrl: null, blob: null, fresh: false })

/** Where a piece at `blockId` goes: after every piece at or before its block. */
function insertClip(clips: DraftClip[], order: string[], clip: DraftClip): DraftClip[] {
  const pos = new Map(order.map((id, i) => [id, i]))
  const target = pos.get(clip.blockId) ?? Infinity
  let at = clips.length
  for (let i = 0; i < clips.length; i++) {
    const p = pos.get(clips[i].blockId)
    if (p !== undefined && p > target) {
      at = i
      break
    }
  }
  return [...clips.slice(0, at), clip, ...clips.slice(at)]
}

export class RecordingSession {
  phase: RecordPhase = 'idle'
  clips: DraftClip[]
  /** The paragraph being recorded (or that recording would start at). */
  currentId: string | null = null
  /** The stretch this run records over. */
  range: { from: string; until: string | null } | null = null
  error: SessionError | null = null
  /** A quiet explanation (the microphone stopped, a piece hit the limit). */
  note: string | null = null
  /** Bumped on every change, for React. */
  version = 0

  private readonly initialIds: string
  private mic: Microphone | null = null
  private take: { rec: Recorder; clipId: string } | null = null
  /** Pieces set aside by the current run, restored if it records nothing. */
  private setAside: DraftClip[] = []
  private readonly processing = new Set<Promise<void>>()
  private readonly uploads = new Map<string, Promise<void>>()
  private readonly urls: string[] = []
  private readonly listeners = new Set<() => void>()
  private readonly levelListeners = new Set<(level: number) => void>()
  private ticker: ReturnType<typeof setInterval> | undefined
  /** Bumped by discard/dispose so late async work is dropped. */
  private gen = 0
  private disposed = false

  constructor(
    initial: NarrationClip[],
    private readonly cfg: SessionConfig,
  ) {
    this.clips = initial.map(saved)
    this.initialIds = this.signature()
  }

  // ── reading ────────────────────────────────────────────────────────────

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  /** The live input level (0..1, ~20 times a second) while the microphone is open. */
  onLevel(fn: (level: number) => void): () => void {
    this.levelListeners.add(fn)
    return () => this.levelListeners.delete(fn)
  }

  /** Seconds of the piece being recorded now. */
  elapsed(): number {
    return this.take?.rec.elapsed() ?? 0
  }

  get recording(): boolean {
    return this.phase === 'recording'
  }

  /** Something here isn't saved: a take in progress, or pieces that differ from the saved ones. */
  get unsaved(): boolean {
    return this.take !== null || this.phase === 'starting' || this.signature() !== this.initialIds
  }

  /** Pieces recorded in this session. */
  get freshCount(): number {
    return this.clips.filter((c) => c.fresh).length
  }

  get uploading(): { done: number; total: number } {
    const fresh = this.clips.filter((c) => c.fresh)
    return { done: fresh.filter((c) => c.upload === 'done').length, total: fresh.length }
  }

  // ── recording ──────────────────────────────────────────────────────────

  /** Start recording at `fromId`, replacing the pieces that start in [fromId, untilId). */
  async begin(fromId: string, untilId: string | null): Promise<void> {
    const busy: RecordPhase[] = ['starting', 'recording', 'finishing', 'saving']
    if (busy.includes(this.phase) || this.disposed) return
    const before = this.phase
    const gen = this.gen
    this.error = null
    this.note = null
    this.phase = 'starting'
    this.currentId = fromId
    this.changed()
    if (!(await this.ensureMic(gen))) {
      if (gen === this.gen) {
        this.phase = before === 'paused' ? 'review' : before
        this.changed()
      }
      return
    }
    if (gen !== this.gen) return
    this.range = { from: fromId, until: untilId }
    const order = this.cfg.order()
    const kept = spliceClips(this.clips, order, fromId, untilId, [])
    this.setAside = this.clips.filter((c) => !kept.includes(c))
    this.clips = kept
    this.startTake(fromId)
  }

  /**
   * Close the current piece and start the next at `toId` (default: the next
   * narrated block). Past the end of the stretch it finishes instead. Going
   * back is refused (false): stop first to re-record earlier paragraphs.
   */
  next(toId?: string): boolean {
    if (this.phase !== 'recording' || !this.take || !this.mic || !this.currentId) return false
    const order = this.cfg.order()
    const target = toId ?? nextInOrder(order, this.currentId)
    const pos = (id: string | null) => (id === null ? Infinity : order.indexOf(id))
    if (target === null || (this.range?.until != null && pos(target) >= pos(this.range.until))) {
      void this.stop()
      return true
    }
    if (pos(target) <= pos(this.currentId)) return false
    const closing = this.take
    let next: Recorder
    let finished: Promise<{ blob: Blob; duration: number }>
    try {
      ;({ finished, next } = this.mic.next(closing.rec, { onInterrupt: () => void this.stop('interrupted') }))
    } catch (e) {
      this.take = null
      this.close(closing.rec.stop(), closing.clipId)
      this.fail(e)
      void this.stop()
      return false
    }
    this.close(finished, closing.clipId)
    this.addPlaceholder(next, target)
    return true
  }

  /** End the current piece; the microphone stays open for resume(). */
  pause(): void {
    if (this.phase !== 'recording' || !this.take) return
    const closing = this.take
    this.take = null
    this.stopTicker()
    this.close(closing.rec.stop(), closing.clipId)
    this.phase = 'paused'
    this.changed()
  }

  /**
   * A new piece at the current paragraph. Resuming on an earlier paragraph
   * than the last piece of this run records over it and what follows.
   */
  resume(): void {
    if (this.phase !== 'paused' || !this.currentId || !this.range) return
    const order = this.cfg.order()
    const cur = order.indexOf(this.currentId)
    const lastInRun = [...this.clips].reverse().find((c) => c.fresh && order.indexOf(c.blockId) >= 0)
    if (lastInRun && cur >= 0 && cur < order.indexOf(lastInRun.blockId)) {
      this.clips = spliceClips(this.clips, order, this.currentId, this.range.until, [])
    }
    if (!this.mic || this.mic.released) {
      void this.begin(this.currentId, this.range.until)
      return
    }
    this.startTake(this.currentId)
  }

  /** The paragraph recording starts (or resumes) at. Not while a take is running. */
  setCurrent(blockId: string | null): void {
    if (this.phase === 'recording' || this.phase === 'starting') return
    this.currentId = blockId
    this.changed()
  }

  /** Finish: close the take, let the pieces finish processing, hand the microphone back, review. */
  async stop(reason?: 'limit' | 'interrupted'): Promise<void> {
    if (this.phase !== 'recording' && this.phase !== 'paused') return
    const gen = this.gen
    if (this.take) {
      const closing = this.take
      this.take = null
      this.close(closing.rec.stop(), closing.clipId)
    }
    this.stopTicker()
    if (reason === 'limit') this.note = `A piece can run up to ${formatLimit(this.cfg.maxClipSeconds)}, so recording stopped there.`
    if (reason === 'interrupted') this.note = 'The microphone stopped. What was recorded is kept.'
    this.phase = 'finishing'
    this.changed()
    await this.settle()
    if (gen !== this.gen) return
    this.releaseMic()
    // A run that recorded nothing gives back what it set aside.
    if (this.range && !this.clips.some((c) => c.fresh && this.inRange(c))) {
      for (const c of this.setAside) this.clips = insertClip(this.clips, this.cfg.order(), c)
    }
    this.setAside = []
    this.phase = 'review'
    this.changed()
  }

  /** Throw away everything recorded in this session (back to the saved pieces). */
  discard(initial: NarrationClip[]): void {
    this.gen++
    this.take?.rec.cancel()
    this.take = null
    this.stopTicker()
    this.releaseMic()
    this.revokeUrls()
    this.clips = initial.map(saved)
    this.setAside = []
    this.range = null
    this.error = null
    this.note = null
    this.phase = 'idle'
    this.changed()
  }

  /** Try the pieces whose upload failed again. */
  retryUploads(): void {
    this.error = null
    for (const c of this.clips) if (c.fresh && c.upload === 'failed' && c.blob) this.upload(c.id, c.blob)
    this.changed()
  }

  /** Remove one fresh piece (e.g. an accidental tap). */
  removeClip(clipId: string): void {
    if (this.phase === 'recording' || this.phase === 'saving') return
    this.clips = this.clips.filter((c) => c.id !== clipId)
    this.changed()
  }

  /**
   * Wait for every piece to be uploaded (retrying failures once more) and
   * return them as a narration's clips; null when some still failed.
   */
  async finalize(): Promise<NarrationClip[] | null> {
    if (this.phase === 'recording' || this.phase === 'paused') await this.stop()
    const gen = this.gen
    this.phase = 'saving'
    this.error = null
    this.changed()
    await this.settle()
    for (const c of this.clips) if (c.fresh && c.upload === 'failed' && c.blob) this.upload(c.id, c.blob)
    await Promise.all([...this.uploads.values()])
    if (gen !== this.gen) return null
    const failed = this.clips.filter((c) => c.fresh && c.upload !== 'done')
    if (failed.length > 0) {
      // The upload's own message when there is one (set as it failed).
      const reason = (this.error as SessionError | null)?.message
      this.phase = 'review'
      this.error = {
        message: reason ?? `${failed.length === 1 ? 'A piece' : `${failed.length} pieces`} didn’t upload. Check your connection and try Save again.`,
      }
      this.changed()
      return null
    }
    return this.clips.map(({ id, blockId, src, duration, peaks, recordedAt }) => ({
      id,
      blockId,
      src,
      duration,
      ...(peaks ? { peaks } : {}),
      ...(recordedAt ? { recordedAt } : {}),
    }))
  }

  /** Stop everything and hand the microphone back (leaving the mode). */
  dispose(): void {
    this.gen++
    this.disposed = true
    this.take?.rec.cancel()
    this.take = null
    this.stopTicker()
    this.releaseMic()
    this.revokeUrls()
    this.listeners.clear()
    this.levelListeners.clear()
  }

  // ── internals ──────────────────────────────────────────────────────────

  private changed(): void {
    this.version++
    this.listeners.forEach((fn) => fn())
  }

  private signature(): string {
    return this.clips.map((c) => c.id).join(',')
  }

  private inRange(c: DraftClip): boolean {
    if (!this.range) return false
    const order = this.cfg.order()
    const i = order.indexOf(c.blockId)
    const from = order.indexOf(this.range.from)
    const until = this.range.until === null ? Infinity : order.indexOf(this.range.until)
    return i >= 0 && i >= from && (until < 0 || i < until)
  }

  private fail(e: unknown): void {
    const err = toRecorderError(e)
    this.error = { message: err.message, code: err.code }
  }

  private async ensureMic(gen: number): Promise<boolean> {
    if (this.mic && !this.mic.released) return true
    try {
      const mic = await openMicrophone({ onLevel: (l) => this.levelListeners.forEach((fn) => fn(l)) })
      if (gen !== this.gen || this.disposed) {
        mic.release()
        return false
      }
      this.mic = mic
      return true
    } catch (e) {
      if (gen === this.gen) this.fail(e)
      return false
    }
  }

  private startTake(blockId: string): void {
    if (!this.mic) return
    let rec: Recorder
    try {
      rec = this.mic.record({ onInterrupt: () => void this.stop('interrupted') })
    } catch (e) {
      this.fail(e)
      this.releaseMic()
      this.phase = this.clips.some((c) => c.fresh) ? 'review' : 'idle'
      this.changed()
      return
    }
    this.addPlaceholder(rec, blockId)
  }

  private addPlaceholder(rec: Recorder, blockId: string): void {
    const clip: DraftClip = {
      id: newBlockId().replace(/^b/, 'c'),
      blockId,
      src: '',
      duration: 0,
      recordedAt: new Date().toISOString(),
      status: 'recording',
      upload: 'waiting',
      localUrl: null,
      blob: null,
      fresh: true,
    }
    this.clips = insertClip(this.clips, this.cfg.order(), clip)
    this.take = { rec, clipId: clip.id }
    this.currentId = blockId
    this.phase = 'recording'
    this.startTicker()
    this.changed()
  }

  /** A take was told to stop: when it's in, measure it, keep it, and upload it. */
  private close(finished: Promise<{ blob: Blob; duration: number }>, clipId: string): void {
    const gen = this.gen
    this.patch(clipId, { status: 'processing' })
    const work = (async () => {
      let r: { blob: Blob; duration: number }
      try {
        r = await finished
      } catch {
        if (gen === this.gen) this.drop(clipId, 'A piece couldn’t be finished, so it was left out.')
        return
      }
      if (gen !== this.gen) return
      if (r.blob.size === 0) {
        this.drop(clipId, 'Nothing was recorded for a piece. Check your microphone.')
        return
      }
      const a = await analyseAudio(r.blob, { fallbackDuration: r.duration })
      if (gen !== this.gen) return
      const localUrl = typeof URL.createObjectURL === 'function' ? URL.createObjectURL(r.blob) : null
      if (localUrl) this.urls.push(localUrl)
      this.patch(clipId, { status: 'ready', duration: a.duration || r.duration, peaks: a.peaks, blob: r.blob, localUrl })
      this.upload(clipId, r.blob)
    })()
    this.processing.add(work)
    void work.finally(() => this.processing.delete(work))
  }

  private upload(clipId: string, blob: Blob): void {
    const gen = this.gen
    this.patch(clipId, { upload: 'uploading' })
    const job = (async () => {
      try {
        assertUploadableAudio(blob)
        const src = await this.cfg.upload(blob)
        if (gen === this.gen) this.patch(clipId, { src, upload: 'done' })
      } catch (e) {
        if (gen !== this.gen) return
        this.patch(clipId, { upload: 'failed' })
        this.error = { message: errorText(e) }
        this.changed()
      }
    })()
    this.uploads.set(clipId, job)
  }

  private async settle(): Promise<void> {
    while (this.processing.size > 0) await Promise.all([...this.processing])
  }

  private patch(clipId: string, patch: Partial<DraftClip>): void {
    if (!this.clips.some((c) => c.id === clipId)) return
    this.clips = this.clips.map((c) => (c.id === clipId ? { ...c, ...patch } : c))
    this.changed()
  }

  private drop(clipId: string, note: string): void {
    this.clips = this.clips.filter((c) => c.id !== clipId)
    this.note = note
    this.changed()
  }

  private startTicker(): void {
    this.stopTicker()
    this.ticker = setInterval(() => {
      if (!this.take) return
      if (this.take.rec.elapsed() >= this.cfg.maxClipSeconds) void this.stop('limit')
      else this.changed()
    }, 250)
  }

  private stopTicker(): void {
    clearInterval(this.ticker)
    this.ticker = undefined
  }

  private releaseMic(): void {
    this.mic?.release()
    this.mic = null
  }

  private revokeUrls(): void {
    if (typeof URL.revokeObjectURL === 'function') this.urls.forEach((u) => URL.revokeObjectURL(u))
    this.urls.length = 0
  }
}
